import { Input } from "antd";
import { useFormik } from "formik";
import { AlertCircle, ArrowLeft, CheckCircle2, Lock, Mail } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  useForgotPasswordMutation,
  useForgotPasswordValidateMutation,
} from "../api/api";
import { useLayoutContext } from "../providers/layout.provider";
import { IStatusMsg } from "../types/types";

export default function ForgotPassword2() {
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();
  const { mutateAsync, isLoading, isError } = useForgotPasswordMutation();
  const { mutateAsync: validateForgotPasswordAsync, isLoading: isPending } =
    useForgotPasswordValidateMutation();

  const [statusMsg, setStatusMsg] = useState<IStatusMsg | undefined>();
  const [email, setEmail] = useState<string>("");
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [passcode, setPasscode] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [retypeNewPassword, setRetypeNewPassword] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please enter email."),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      mutateAsync({ email: values.email })
        .then((resp) => {
          setEmail(values.email);
          setStepIndex(1);
          setStatusMsg({
            type: "success",
            text: "We have sent a verification code to this email.",
          });
        })
        .catch((e) => {
          if (e.response?.status === 403) {
            setStatusMsg({
              type: "error",
              text: "You have exceeded the number of attempts. Please contact the admin.",
            });
          } else {
            setStatusMsg({
              type: "error",
              text: "This email does not exist in the system. Please try again.",
            });
          }
        });
    },
  });

  const handleChangePasswordAsync = () => {
    validateForgotPasswordAsync({
      email: formik.values.email,
      passcode: +passcode,
      newPassword,
    })
      .then((resp) => {
        formik.resetForm();
        setEmail("");
        setStepIndex(3);
        setStatusMsg({
          type: "success",
          text: "Your password is successfully changed",
        });
      })
      .catch((e) => {
        if (e.response?.status === 401) {
          setStatusMsg({
            type: "error",
            text: "The passcode is incorrect. Please go back and try again.",
          });
        } else {
          setStatusMsg({
            type: "error",
            text: "Whoops! Something went wrong. Please try again.",
          });
        }
      });
  };

  const newPasswordError = useMemo(() => {
    if (newPassword) {
      if (newPassword.length < 6) {
        return "Password should atleast be 6 chars";
      }
      return undefined;
    }
    return "Please enter password";
  }, [newPassword]);

  const retypeNewPasswordError = useMemo(() => {
    if (newPassword) {
      if (retypeNewPassword) {
        if (retypeNewPassword !== newPassword) {
          return "Passwords are not identical";
        }
        return undefined;
      }
      return "Please retype password";
    }
    return undefined;
  }, [retypeNewPassword, newPassword]);

  const passcodeError = useMemo(() => {
    if (passcode) {
      if (passcode.length < 6) {
        return "Passcode should be 6 digits";
      }
      return undefined;
    }
    return "Please enter passcode";
  }, [passcode]);

  return (
    <div className="min-h-screen bg-white mx-auto max-w-5xl">
      <main className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
        <div>
          {/* Header Section */}
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() =>
                stepIndex === 0 ? navigate("/") : setStepIndex(1)
              }
              className="p-2 hover:bg-slate-200 rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Reset Password
              </h1>
              <p className="text-muted-foreground">
                {stepIndex === 0 && "Enter your email to get started"}
                {stepIndex === 1 && "Enter the OTP we sent you"}
              </p>
            </div>
          </div>

          {/* Forgot Password Form */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
            {/* Success Alert */}
            {statusMsg && statusMsg.type === "success" && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700 font-medium">
                  {statusMsg.text}
                </p>
              </div>
            )}

            {/* Error Alert */}
            {statusMsg && statusMsg.type === "error" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{statusMsg.text}</p>
              </div>
            )}

            {/* Step 1: Email Input */}
            {stepIndex === 0 && (
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Progress Indicator */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      Enter Email
                    </span>
                  </div>
                  <div className="flex-1 mx-3 h-1 bg-slate-200"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Verify OTP
                    </span>
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-foreground mb-2"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 ">
                    <Mail className="w-5 h-5 text-muted-foreground pointer-events-none" />
                    <Input
                      id="email"
                      {...formik.getFieldProps("email")}
                      size="large"
                      disabled={stepIndex > 0}
                      placeholder="Enter your email address..."
                      status={!!formik.errors.email ? "error" : undefined}
                    />
                  </div>
                  {formik.errors.email && (
                    <p className="text-sm text-red-600 mt-2">
                      {formik.errors.email}
                    </p>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <span className="font-semibold">
                      We'll send an OTP to your email address.
                    </span>{" "}
                    Make sure you have access to this email.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  id="send-otp-btn"
                  disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending OTP..." : "Send OTP"}
                </button>
              </form>
            )}

            {/* Step 2: OTP Input */}
            {stepIndex === 1 && (
              <div className="space-y-6">
                {/* Progress Indicator */}

                {/* Email Display */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-muted-foreground mb-1">
                    Email sent to:
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {email}
                  </p>
                </div>

                {/* OTP Input */}
                <div>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-semibold text-foreground mb-2"
                  >
                    One-Time Passcode <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder="000000"
                      inputMode="numeric"
                      maxLength={6}
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value)}
                      className={`pl-10 h-11 text-center text-2xl tracking-widest font-mono ${
                        passcodeError ? "border-red-500" : ""
                      }`}
                    />
                  </div>
                  {passcodeError && (
                    <p className="text-sm text-red-600 mt-2">{passcodeError}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Check your email for the 6-digit code
                  </p>
                </div>

                {/* Resend OTP */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Didn't receive the code?
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setStepIndex(0);
                      setPasscode("");
                    }}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    Go back and try another email
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="button"
                  onClick={() => {
                    setStepIndex(2);
                    setStatusMsg(undefined);
                  }}
                  id="continue-btn"
                  disabled={passcode.trim().length === 0}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify & Continue
                </button>
              </div>
            )}

            {stepIndex === 2 && (
              <div className="space-y-6">
                <p className="text-xs">Please enter new password</p>
                {/* Progress Indicator */}
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-semibold text-foreground mb-2"
                  >
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 ">
                    <Input
                      id="newPassword"
                      value={newPassword}
                      type="password"
                      onChange={(e) => setNewPassword(e.target.value)}
                      size="large"
                      placeholder="Enter your new password..."
                      status={!!newPasswordError ? "error" : undefined}
                    />
                  </div>
                  {newPasswordError && (
                    <p className="text-sm text-red-600 mt-2">
                      {newPasswordError}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="retypePassword"
                    className="block text-sm font-semibold text-foreground mb-2"
                  >
                    Re-enter Password <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 ">
                    <Input
                      id="retypePassword"
                      type="password"
                      value={retypeNewPassword}
                      onChange={(e) => setRetypeNewPassword(e.target.value)}
                      size="large"
                      placeholder="Enter your new password..."
                      status={!!retypeNewPasswordError ? "error" : undefined}
                    />
                  </div>
                  {retypeNewPasswordError && (
                    <p className="text-sm text-red-600 mt-2">
                      {retypeNewPasswordError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  id="continue-btn"
                  onClick={() => handleChangePasswordAsync()}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPending ? "Processing..." : "Submit"}
                </button>
              </div>
            )}

            {stepIndex === 3 && (
              <div className="space-y-6">
                <p className="text-sm">
                  Please click the respective application links to continue
                </p>
                {/* Progress Indicator */}
                <div className="grid grid-cols-2 gap-10">
                  <button
                    className="border dashed py-1 bg-gradient-to-r from-purple-500 to-purple-800 text-white rounded-md text-sm"
                    onClick={() => {
                      window.location.href = "https://app.expensehut.com/login";
                    }}
                  >
                    Expense Tracker
                  </button>

                  <button
                    className="border dashed py-1 bg-gradient-to-r from-blue-500 to-blue-800 text-white rounded-md text-sm"
                    onClick={() => {
                      window.location.href =
                        "https://business.expensehut.com/login";
                    }}
                  >
                    Point Of Sale
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer Text */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            Remember your password?{" "}
            <button
              onClick={() => navigate("/login")}
              className="text-primary font-semibold hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
